import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Options accepted by close handlers wired to `useHistoryDismiss`.
 * `viaNavigation` marks a close that a navigation (router.push / pushState)
 * immediately follows, so the hook skips its reconciling `history.back()` and
 * doesn't race the navigation. Compatible with the mobile drawer's
 * `SidebarCloseOptions` (ORI-464).
 */
export type HistoryDismissCloseOptions = { viaNavigation?: boolean };

export type HistoryDismissConfig = {
  /**
   * Push the marker history entry automatically when `isOpen` flips to true
   * (default). Set `false` to push imperatively via the returned `pushEntry()`
   * from the open handler instead — required when the open state can also be
   * set by flows that must NOT create a history entry (e.g. the sidebar
   * drawer's direct `setExpandedForMobile(true)` from the resize path).
   */
  autoPush?: boolean;
  /**
   * Gate the popstate listener on our marker actually having been popped off
   * `window.history.state` (default). The panel URL helpers in
   * `src/helper/utils.ts` dispatch SYNTHETIC `PopStateEvent`s (pushState +
   * dispatchEvent — no real pop); with the gate on, a popstate only fires
   * `onClose` when a real Back has removed our marker entry. Set `false` for
   * the mobile drawer's original ORI-464 dialect (any popstate while open
   * dismisses it).
   */
  requireMarkerPopped?: boolean;
};

export type HistoryDismissHandle = {
  /**
   * Flag an imminent navigation (router.push / manual pushState) so the hook
   * neither reconciles with `history.back()` on the following close nor treats
   * a synthetic popstate from that navigation as a hardware Back. Cleared on
   * the next open/close transition.
   */
  markNavigating: () => void;
  /**
   * Imperative marker push for `autoPush: false` consumers; call it from the
   * open handler before setting the open state. Guarded on a synchronous ref,
   * so two same-tick opens cannot double-push.
   */
  pushEntry: () => void;
};

/**
 * Makes an overlay (side panel / drawer) participate in browser history so a
 * hardware/browser Back dismisses IT instead of popping the entry underneath
 * and closing the parent surface (ORI-459; lifted from the ORI-464 drawer fix
 * in SidebarToggleContext).
 *
 * Opening pushes ONE history entry whose state carries `{ [historyKey]: true }`
 * merged over the current state — no url argument (search params like
 * reqId/woId stay put, so the vendor detail-panel popstate listeners are
 * undisturbed) and no synthetic PopStateEvent. A real Back pops that entry and
 * the popstate listener fires `onClose`; a manual close (isOpen true→false)
 * reconciles the pushed entry via `history.back()` when our marker is still on
 * top and no navigation was flagged.
 *
 * Next.js doesn't support location.state; `window.history.state` is the
 * fallback marker surface.
 */
const useHistoryDismiss = (
  isOpen: boolean,
  onClose: (options?: HistoryDismissCloseOptions) => void,
  historyKey: string,
  config?: HistoryDismissConfig,
): HistoryDismissHandle => {
  const { autoPush = true, requireMarkerPopped = true } = config ?? {};

  // Live mirrors so the mount-once popstate listener reads current values
  // without re-registering.
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const keyRef = useRef(historyKey);
  const gateRef = useRef(requireMarkerPopped);
  // Track the previous open state so the transition effect only acts on real
  // false→true / true→false flips.
  const prevOpenRef = useRef(false);
  // Whether we pushed a history entry for the current open. Synchronous, so
  // the push guard is race-free across same-tick double-opens.
  const pushedRef = useRef(false);
  // Set by markNavigating(): an imminent navigation owns history — skip the
  // reconciling back() and ignore synthetic popstates until the next
  // open/close transition clears it.
  const navigatingRef = useRef(false);
  // Set right before our own reconciling history.back(), so the popstate that
  // back() emits is swallowed instead of double-firing onClose.
  const suppressNextPopRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
    keyRef.current = historyKey;
    gateRef.current = requireMarkerPopped;
  }, [onClose, historyKey, requireMarkerPopped]);

  const isMarkerOnTop = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    const state = window.history.state as Record<string, unknown> | null;
    return state?.[keyRef.current] === true;
  }, []);

  const pushEntry = useCallback(() => {
    if (typeof window === 'undefined' || pushedRef.current) return;
    // Merge over the current state (preserves reqId/woId panel state) with no
    // url argument and no synthetic PopStateEvent.
    window.history.pushState({ ...(window.history.state || {}), [keyRef.current]: true }, '');
    pushedRef.current = true;
  }, []);

  const markNavigating = useCallback(() => {
    navigatingRef.current = true;
  }, []);

  // Register the popstate listener once. On a real Back the browser has
  // already popped our entry, so we only reflect the close here (no history
  // mutation).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePopState = () => {
      // The popstate emitted by our own reconciling back(): consume and skip.
      if (suppressNextPopRef.current) {
        suppressNextPopRef.current = false;
        return;
      }
      // A flagged navigation owns history right now; its pushState + synthetic
      // popstate must not read as a hardware Back.
      if (navigatingRef.current) return;
      if (!isOpenRef.current) return;
      if (gateRef.current) {
        // Marker-gate: only a REAL pop removes our marker from
        // window.history.state. If we never pushed, or the marker is still on
        // top, this popstate is synthetic (src/helper/utils.ts helpers
        // dispatch PopStateEvents without popping) — ignore it.
        if (!pushedRef.current || isMarkerOnTop()) return;
      }
      pushedRef.current = false;
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMarkerOnTop]);

  // Open/close transitions: push the marker entry on open (autoPush mode) and
  // reconcile the pushed entry on manual close.
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    isOpenRef.current = isOpen;
    if (isOpen === wasOpen) return;

    if (isOpen) {
      navigatingRef.current = false;
      if (autoPush) pushEntry();
      return;
    }

    // Manual close (a popstate-driven close already cleared pushedRef): pop
    // our pushed entry so it doesn't orphan on the stack — but only when it is
    // still on top (a buried marker would pop an unrelated entry) and no
    // navigation was flagged (back() would race the imminent push).
    const shouldReconcile = pushedRef.current && !navigatingRef.current && isMarkerOnTop();
    pushedRef.current = false;
    navigatingRef.current = false;
    if (shouldReconcile && typeof window !== 'undefined') {
      suppressNextPopRef.current = true;
      window.history.back();
    }
  }, [isOpen, autoPush, pushEntry, isMarkerOnTop]);

  return useMemo(() => ({ markNavigating, pushEntry }), [markNavigating, pushEntry]);
};

export default useHistoryDismiss;
